import type {
  GameAction,
  GlobalLeaderboardView,
  LeaderboardView,
  MatchView,
  ProfileMedalsView,
  ProfileStatsView,
  ReactionId,
  SeatIndex,
} from "@meksiko/shared-types";
import { createStore } from "zustand/vanilla";
import { isMuted, playMatchUpdateSfx, setMuted } from "../audio/sfx.js";
import { getLanguage, LANGUAGES, setLanguage as setLanguageI18n, t, tError, type LanguageCode } from "../i18n/index.js";
import { updateRotateOverlayText } from "../i18n/rotateOverlay.js";
import { GameConnection, type WaitingRoomInfo } from "../net/connection.js";
import { detectPlatformAdapter, type PlatformAdapter } from "../platform/index.js";

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:2567";
export const WS_BASE_URL = import.meta.env.VITE_WS_BASE_URL ?? "ws://localhost:2567";

export type Screen = "loading" | "login" | "lobby" | "waiting" | "game" | "leaderboard" | "profile" | "error";

// How long a reaction bubble stays visible above a seat before fading out —
// purely a display concern (see AppState.activeReactions below).
const REACTION_DISPLAY_MS = 3500;

interface ActiveReaction {
  reactionId: ReactionId;
  // Distinguishes this specific reaction instance from the next one the same
  // seat might send before this one's timer fires, so a fast repeat reaction
  // doesn't get wiped early by the first one's stale setTimeout.
  token: number;
}

interface AppState {
  screen: Screen;
  platform: PlatformAdapter | null;
  connection: GameConnection | null;

  authToken: string | null;
  userId: string | null;
  displayName: string;
  photoUrl: string | null;
  coinBalance: number;
  matchEntryFee: number;
  /** A still-running match this user has a seat in (they left/disconnected and got AI-replaced) — see GET /api/me. */
  activeMatchId: string | null;

  matchView: MatchView | null;
  waiting: WaitingRoomInfo | null;
  lastError: string | null;
  showInsufficientFundsModal: boolean;
  reactionMenuOpen: boolean;
  activeReactions: Partial<Record<SeatIndex, ActiveReaction>>;
  rulesOpen: boolean;
  settingsOpen: boolean;
  deleteAccountOpen: boolean;
  deleteAccountPending: boolean;
  deleteAccountError: string | null;
  muted: boolean;
  displayNameError: string | null;
  language: LanguageCode;
  languageMenuOpen: boolean;

  leaderboard: LeaderboardView | null;
  globalLeaderboard: GlobalLeaderboardView | null;
  leaderboardTab: "season" | "global";
  profileMedals: ProfileMedalsView | null;
  profileStats: ProfileStatsView | null;

  bootstrap(): Promise<void>;
  login(connection?: "facebook" | "google-oauth2"): void;
  joinQueue(): Promise<void>;
  closeInsufficientFundsModal(): void;
  sendGameAction(action: GameAction): void;
  watchAd(): Promise<void>;
  refreshMe(): Promise<void>;
  leaveMatch(): void;
  dismissMatchEnd(): void;
  toggleReactionMenu(): void;
  closeReactionMenu(): void;
  sendReaction(reactionId: ReactionId): void;
  /** Internal: records an incoming reaction (own or another seat's) so it renders as a bubble, then auto-clears it. */
  showReaction(seat: SeatIndex, reactionId: ReactionId): void;
  refreshLeaderboard(): Promise<void>;
  refreshGlobalLeaderboard(): Promise<void>;
  openLeaderboardScreen(): void;
  setLeaderboardTab(tab: "season" | "global"): void;
  openProfileScreen(): Promise<void>;
  backToLobby(): void;
  shareInvite(): void;
  openRules(): void;
  closeRules(): void;
  openSettings(): void;
  closeSettings(): void;
  openDeleteAccount(): void;
  closeDeleteAccount(): void;
  deleteAccount(): Promise<void>;
  logout(): Promise<void>;
  toggleMute(): void;
  updateDisplayName(name: string): Promise<void>;
  toggleLanguageMenu(): void;
  closeLanguageMenu(): void;
  setLanguage(lang: LanguageCode): void;
}

export const useAppStore = createStore<AppState>((set, get) => ({
  screen: "loading",
  platform: null,
  connection: null,
  authToken: null,
  userId: null,
  displayName: "",
  photoUrl: null,
  coinBalance: 0,
  matchEntryFee: 10,
  activeMatchId: null,
  matchView: null,
  waiting: null,
  lastError: null,
  showInsufficientFundsModal: false,
  reactionMenuOpen: false,
  activeReactions: {},
  rulesOpen: false,
  settingsOpen: false,
  deleteAccountOpen: false,
  deleteAccountPending: false,
  deleteAccountError: null,
  muted: isMuted(),
  displayNameError: null,
  language: getLanguage(),
  languageMenuOpen: false,

  leaderboard: null,
  globalLeaderboard: null,
  leaderboardTab: "season",
  profileMedals: null,
  profileStats: null,

  async bootstrap() {
    try {
      const platform = detectPlatformAdapter();
      await platform.init();
      set({ platform });
      if (!(await platform.isAuthenticated())) {
        set({ screen: "login" });
        return;
      }
      const auth = await platform.authenticate(API_BASE_URL);
      const meRes = await fetch(`${API_BASE_URL}/api/me`, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      const me = meRes.ok ? await meRes.json() : null;
      set({
        authToken: auth.token,
        userId: auth.userId,
        displayName: me?.displayName ?? auth.displayName,
        photoUrl: me?.photoUrl ?? auth.photoUrl,
        coinBalance: me?.coinBalance ?? auth.coinBalance,
        matchEntryFee: me?.matchEntryFee ?? 10,
        activeMatchId: me?.activeMatchId ?? null,
        screen: "lobby",
      });
      void get().refreshLeaderboard();
    } catch (err) {
      set({ screen: "error", lastError: (err as Error).message });
    }
  },

  login(connection) {
    get().platform?.login(connection);
  },

  async joinQueue() {
    const { authToken, coinBalance, matchEntryFee, activeMatchId } = get();
    if (!authToken) return;
    // The entry-fee/balance gate only applies to starting a NEW match —
    // rejoining one already paid for (see GET /api/me's activeMatchId)
    // never charges again server-side either (handleRejoin in MeksikoRoom.ts).
    if (!activeMatchId && coinBalance < matchEntryFee) {
      set({ showInsufficientFundsModal: true, lastError: null });
      return;
    }
    const connection = new GameConnection(WS_BASE_URL);
    set({ connection, screen: "waiting", lastError: null });
    try {
      await connection.join(
        authToken,
        {
          onState: (view) => {
            playMatchUpdateSfx(get().matchView, view);
            set({ matchView: view, screen: "game", waiting: null });
          },
          onWaiting: (info) => set({ waiting: info }),
          onActionRejected: (code) => set({ lastError: tError(code) }),
          onMatchStartFailed: (reason) => set({ lastError: tError(reason), screen: "lobby", connection: null }),
          onReaction: (seat, reactionId) => get().showReaction(seat, reactionId),
          onDisconnected: () => set({ screen: "lobby", connection: null, matchView: null, waiting: null, activeReactions: {}, reactionMenuOpen: false }),
        },
        activeMatchId,
      );
    } catch (err) {
      set({ screen: "lobby", lastError: tError((err as Error).message), connection: null });
    }
  },

  closeInsufficientFundsModal() {
    set({ showInsufficientFundsModal: false });
  },

  sendGameAction(action) {
    get().connection?.sendAction(action);
  },

  async watchAd() {
    const { platform, authToken } = get();
    if (!platform || !authToken) return;
    const startRes = await fetch(`${API_BASE_URL}/api/economy/ad/start`, {
      method: "POST",
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!startRes.ok) return;
    const { ticketId } = await startRes.json();

    const watched = await platform.showRewardedAd();
    // Previously a silent no-op here — from the player's side the "Watch ad"
    // button just looked dead (reported 2026-09-09, root cause was a browser
    // ad blocker; FBInstant gives no reliable code to tell that apart from a
    // genuine no-fill/network failure — see the comment in facebook.ts). A
    // generic retry hint at least confirms something happened.
    if (!watched) {
      set({ lastError: tError("ad_unavailable") });
      return;
    }

    const claimRes = await fetch(`${API_BASE_URL}/api/economy/ad/claim`, {
      method: "POST",
      headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({ ticketId }),
    });
    if (claimRes.ok) {
      const { coinBalance } = await claimRes.json();
      set({ coinBalance });
      // The only place watchAd() is triggered from right now — close it
      // automatically once the reward covers the entry fee so the player can
      // just hit "Igraj" again instead of an extra "Zatvori" click.
      if (coinBalance >= get().matchEntryFee) set({ showInsufficientFundsModal: false });
    } else {
      const body = await claimRes.json().catch(() => null);
      set({ lastError: tError(body?.error ?? "ad_unavailable") });
    }
  },

  async refreshMe() {
    const { authToken } = get();
    if (!authToken) return;
    const res = await fetch(`${API_BASE_URL}/api/me`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (res.ok) {
      const me = await res.json();
      set({ coinBalance: me.coinBalance, matchEntryFee: me.matchEntryFee, activeMatchId: me.activeMatchId ?? null });
    }
  },

  leaveMatch() {
    get().connection?.leaveMatch();
    get().connection?.disconnect();
    set({ connection: null, matchView: null, waiting: null, screen: "lobby", activeReactions: {}, reactionMenuOpen: false });
    void get().refreshLeaderboard();
    void get().refreshMe();
  },

  dismissMatchEnd() {
    get().connection?.disconnect();
    // Only ever called from the match-ended screen (view.matchEnded), so the
    // match is unambiguously over — clear activeMatchId locally right away
    // instead of waiting on refreshMe()'s round-trip, otherwise the lobby
    // button is stuck on "Resume match" until that fetch resolves (or stays
    // stuck indefinitely if it silently fails).
    set({
      connection: null,
      matchView: null,
      waiting: null,
      screen: "lobby",
      activeReactions: {},
      reactionMenuOpen: false,
      activeMatchId: null,
    });
    void get().refreshLeaderboard();
    void get().refreshMe();
  },

  toggleReactionMenu() {
    set((state) => ({ reactionMenuOpen: !state.reactionMenuOpen }));
  },

  closeReactionMenu() {
    set({ reactionMenuOpen: false });
  },

  sendReaction(reactionId) {
    get().connection?.sendReaction(reactionId);
    set({ reactionMenuOpen: false });
  },

  showReaction(seat, reactionId) {
    const token = Date.now() + Math.random();
    set((state) => ({ activeReactions: { ...state.activeReactions, [seat]: { reactionId, token } } }));
    setTimeout(() => {
      if (get().activeReactions[seat]?.token !== token) return; // superseded by a newer reaction from the same seat
      const next = { ...get().activeReactions };
      delete next[seat];
      set({ activeReactions: next });
    }, REACTION_DISPLAY_MS);
  },

  async refreshLeaderboard() {
    const { authToken } = get();
    if (!authToken) return;
    const res = await fetch(`${API_BASE_URL}/api/leaderboard`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (res.ok) set({ leaderboard: await res.json() });
  },

  async refreshGlobalLeaderboard() {
    const { authToken } = get();
    if (!authToken) return;
    const res = await fetch(`${API_BASE_URL}/api/leaderboard/global`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (res.ok) set({ globalLeaderboard: await res.json() });
  },

  openLeaderboardScreen() {
    set({ screen: "leaderboard", leaderboardTab: "season" });
    void get().refreshLeaderboard();
  },

  setLeaderboardTab(tab) {
    set({ leaderboardTab: tab });
    if (tab === "global") void get().refreshGlobalLeaderboard();
  },

  async openProfileScreen() {
    const { authToken } = get();
    set({ screen: "profile" });
    if (!authToken) return;
    const headers = { Authorization: `Bearer ${authToken}` };
    const [medalsRes, statsRes] = await Promise.all([
      fetch(`${API_BASE_URL}/api/me/medals`, { headers }),
      fetch(`${API_BASE_URL}/api/me/stats`, { headers }),
    ]);
    set({
      profileMedals: medalsRes.ok ? await medalsRes.json() : null,
      profileStats: statsRes.ok ? await statsRes.json() : null,
    });
  },

  backToLobby() {
    set({ screen: "lobby" });
    void get().refreshLeaderboard();
  },

  shareInvite() {
    const { platform, userId } = get();
    if (!platform || !userId) return;
    void platform.shareInvite(userId);
  },

  openRules() {
    set({ rulesOpen: true });
  },

  closeRules() {
    set({ rulesOpen: false });
  },

  openSettings() {
    set({ settingsOpen: true, displayNameError: null });
  },

  closeSettings() {
    set({ settingsOpen: false, displayNameError: null });
  },

  openDeleteAccount() {
    set({ deleteAccountOpen: true, deleteAccountError: null });
  },

  closeDeleteAccount() {
    set({ deleteAccountOpen: false, deleteAccountError: null });
  },

  async deleteAccount() {
    const { authToken, platform } = get();
    if (!authToken) return;
    set({ deleteAccountPending: true, deleteAccountError: null });
    const res = await fetch(`${API_BASE_URL}/api/me`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!res.ok) {
      const body: { error?: string } | null = await res.json().catch(() => null);
      set({
        deleteAccountPending: false,
        deleteAccountError:
          body?.error === "active_match_in_progress" ? t("profile.deleteAccountActiveMatch") : t("profile.deleteAccountFailed"),
      });
      return;
    }
    await platform?.logout();
    // Reset to a fresh logged-out state — same shape as the initial store,
    // minus screen/UI-preference fields (language, muted) a deleted account
    // shouldn't reset either.
    set({
      screen: "login",
      authToken: null,
      userId: null,
      displayName: "",
      photoUrl: null,
      coinBalance: 0,
      matchEntryFee: 10,
      activeMatchId: null,
      matchView: null,
      leaderboard: null,
      globalLeaderboard: null,
      profileMedals: null,
      profileStats: null,
      deleteAccountOpen: false,
      deleteAccountPending: false,
      deleteAccountError: null,
    });
  },

  async logout() {
    const { platform, connection } = get();
    connection?.disconnect();
    await platform?.logout();
    // Same reset shape as deleteAccount() above, minus the account itself
    // (which still exists — this just clears the local session).
    set({
      screen: "login",
      connection: null,
      authToken: null,
      userId: null,
      displayName: "",
      photoUrl: null,
      coinBalance: 0,
      matchEntryFee: 10,
      activeMatchId: null,
      matchView: null,
      leaderboard: null,
      globalLeaderboard: null,
      profileMedals: null,
      profileStats: null,
      settingsOpen: false,
    });
  },

  toggleMute() {
    const next = !get().muted;
    setMuted(next);
    set({ muted: next });
  },

  async updateDisplayName(name: string) {
    const { authToken } = get();
    if (!authToken) return;
    const trimmed = name.trim();
    if (trimmed.length < 1 || trimmed.length > 20) {
      set({ displayNameError: t("settings.displayNameLengthError") });
      return;
    }
    const res = await fetch(`${API_BASE_URL}/api/me/display-name`, {
      method: "POST",
      headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({ displayName: trimmed }),
    });
    if (!res.ok) {
      set({ displayNameError: t("settings.saveFailed") });
      return;
    }
    const { displayName } = await res.json();
    set({ displayName, displayNameError: null });
  },

  toggleLanguageMenu() {
    set((state) => ({ languageMenuOpen: !state.languageMenuOpen }));
  },

  closeLanguageMenu() {
    set({ languageMenuOpen: false });
  },

  setLanguage(lang) {
    setLanguageI18n(lang);
    document.documentElement.lang = LANGUAGES.find((l) => l.code === lang)?.bcp47Tag ?? lang;
    updateRotateOverlayText();
    set({ language: lang, languageMenuOpen: false });
  },
}));
